import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import SimulationExecutionPanel from '../../components/planning/SimulationExecutionPanel';

type SimStatus = 'SAVED' | 'APPLIED' | 'DISCARDED';

interface SimulationListItem {
  id: number;
  name: string;
  status: SimStatus;
  objetivo: string | null;
  plan: string | null;
  reglas: string | null;
  setupSavedMinutes: number | null;
  balanceScore: number | null;
  onTimeRate: number | null;
  ordersCount: number | null;
  parentSimulationId: number | null;
  createdBy: string | number | null;
  createdAt: string;
  appliedAt: string | null;
  appliedBy: string | number | null;
}

interface SimulationDetail extends SimulationListItem {
  result: {
    optimized_schedule: Array<{
      order_id: string;
      assigned_machine: string;
      machine_name?: string | null;
      recommended_start: string;
      recommended_end: string;
      sequence: number;
      order_details?: {
        customerCode: string | null;
        customerName: string | null;
        branchName: string | null;
        productCode: string | null;
        productName: string | null;
        requestedKilos: number | null;
        unit: string | null;
        colorsRequired: number | null;
        dueDate: string | null;
      } | null;
    }>;
    analysis?: Record<string, number>;
    recommendations?: string[];
    warnings?: string[];
  } | null;
  policySnapshot: Array<{ id: number; level: number; title: string; instruction: string }> | null;
}

const PAGE_SIZE = 20;

const STATUS_STYLES: Record<SimStatus, { label: string; cls: string }> = {
  SAVED: { label: 'Guardada', cls: 'bg-blue-100 text-blue-700' },
  APPLIED: { label: 'Aplicada', cls: 'bg-green-100 text-green-700' },
  DISCARDED: { label: 'Descartada', cls: 'bg-gray-200 text-gray-600' },
};

const pct = (v: number | null | undefined) =>
  v == null ? '—' : `${(Number(v) * 100).toFixed(0)}%`;

const fmtDate = (v: string | null | undefined) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleString('es-MX');
};

// dueDate is a date-only string (YYYY-MM-DD); format without a timezone shift.
const fmtDay = (v: string | null | undefined) => {
  if (!v) return '—';
  const [y, m, d] = v.slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : v;
};

const jsonHeaders = { 'Content-Type': 'application/json' };

async function readError(res: Response, fallback: string): Promise<string> {
  const body = await res.json().catch(() => ({}));
  return body?.error || fallback;
}

const StatusBadge: React.FC<{ status: SimStatus }> = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[status].cls}`}>
    {STATUS_STYLES[status].label}
  </span>
);

const PlanningSimulations: React.FC = () => {
  const { user } = useAuth();
  const canView = !!user?.permissions?.includes('orders:planning-board');
  const canWrite = !!user?.permissions?.includes('orders:update');

  const [items, setItems] = useState<SimulationListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'' | SimStatus>('');
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<SimulationDetail | null>(null);
  const selectedIdRef = useRef<number | null>(null);
  const [detailTab, setDetailTab] = useState<'resumen' | 'ejecucion'>('resumen');
  const detailReqRef = useRef(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<'apply' | 'discard' | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const qs = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      if (statusFilter) qs.set('status', statusFilter);
      const res = await fetch(`/api/planning/simulations?${qs}`, { credentials: 'include' });
      if (!res.ok) throw new Error(await readError(res, 'Error al cargar simulaciones'));
      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e: any) {
      setListError(e.message);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  const loadDetail = useCallback(async (id: number) => {
    const req = ++detailReqRef.current;
    const isCurrent = () => req === detailReqRef.current && selectedIdRef.current === id;
    setDetailLoading(true);
    setDetailError(null);
    try {
      const res = await fetch(`/api/planning/simulations/${id}`, { credentials: 'include' });
      if (!res.ok) throw new Error(await readError(res, 'Error al cargar la simulación'));
      const data = await res.json();
      if (isCurrent()) setDetail(data.simulation);
    } catch (e: any) {
      if (isCurrent()) {
        setDetailError(e.message);
        setDetail(null);
      }
    } finally {
      if (isCurrent()) setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) loadList();
  }, [canView, loadList]);

  const selectSimulation = (id: number) => {
    if (busy) return;
    selectedIdRef.current = id;
    setSelectedId(id);
    setDetailTab('resumen');
    setDetail(null);
    setConfirming(null);
    setRenaming(false);
    setActionError(null);
    setNotice(null);
    loadDetail(id);
  };

  const refreshAll = async () => {
    const current = selectedIdRef.current;
    await Promise.all([loadList(), current != null ? loadDetail(current) : Promise.resolve()]);
  };

  const runAction = async (fn: () => Promise<void>) => {
    if (!detail || detail.id !== selectedIdRef.current) {
      setActionError('La simulación seleccionada cambió; vuelve a intentarlo.');
      setConfirming(null);
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await fn();
    } catch (e: any) {
      setActionError(e.message);
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  };

  const handleRename = () =>
    runAction(async () => {
      if (!detail) return;
      const res = await fetch(`/api/planning/simulations/${detail.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: jsonHeaders,
        body: JSON.stringify({ name: nameDraft }),
      });
      if (!res.ok) throw new Error(await readError(res, 'Error al renombrar'));
      setRenaming(false);
      await refreshAll();
    });

  const handleApply = () =>
    runAction(async () => {
      if (!detail) return;
      // schedule is required by the endpoint even with simulationId; the
      // backend applies the stored schedule and ignores the body copy.
      const res = await fetch('/api/gantt/apply-optimization', {
        method: 'POST',
        credentials: 'include',
        headers: jsonHeaders,
        body: JSON.stringify({
          schedule: detail.result?.optimized_schedule ?? [],
          simulationId: detail.id,
        }),
      });
      if (!res.ok) throw new Error(await readError(res, 'Error al aplicar la simulación'));
      const data = await res.json().catch(() => ({}));
      const skipped: string[] = Array.isArray(data.skipped) ? data.skipped : [];
      setNotice(
        `Simulación aplicada (${data.appliedCount ?? 0} órdenes).` +
          (skipped.length ? ` Se omitieron ${skipped.length} orden(es) que ya no están autorizadas: ${skipped.join(', ')}` : '')
      );
      await refreshAll();
    });

  const handleDiscard = () =>
    runAction(async () => {
      if (!detail) return;
      const res = await fetch(`/api/planning/simulations/${detail.id}/discard`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(await readError(res, 'Error al descartar la simulación'));
      setNotice('Simulación descartada.');
      await refreshAll();
    });

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-red-600">
          <p className="text-xl font-bold mb-2">Sin acceso</p>
          <p>No tienes permiso para ver las Simulaciones de Planeación.</p>
        </div>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const isSaved = detail?.status === 'SAVED';

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-800">🕘 Simulaciones de Planeación</h1>
        <div className="flex items-center gap-2 text-sm">
          <label htmlFor="sim-status" className="text-gray-600">Estado</label>
          <select
            id="sim-status"
            value={statusFilter}
            onChange={(e) => { setPage(1); setStatusFilter(e.target.value as '' | SimStatus); }}
            className="border border-gray-300 rounded-lg px-2 py-1"
          >
            <option value="">Todos</option>
            <option value="SAVED">Guardadas</option>
            <option value="APPLIED">Aplicadas</option>
            <option value="DISCARDED">Descartadas</option>
          </select>
        </div>
      </div>

      {listError && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{listError}</div>}

      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              {['#', 'Nombre', 'Fecha', 'Estado', 'Órdenes', 'Setup ahorrado (min)', 'Balance', 'A tiempo', 'Creada por'].map((h) => (
                <th key={h} className="px-3 py-2 font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((s) => (
              <tr
                key={s.id}
                onClick={() => selectSimulation(s.id)}
                aria-disabled={busy}
                className={`cursor-pointer hover:bg-purple-50 ${selectedId === s.id ? 'bg-purple-50' : ''}`}
              >
                <td className="px-3 py-2">{s.id}</td>
                <td className="px-3 py-2">{s.name}</td>
                <td className="px-3 py-2 whitespace-nowrap">{fmtDate(s.createdAt)}</td>
                <td className="px-3 py-2"><StatusBadge status={s.status} /></td>
                <td className="px-3 py-2">{s.ordersCount ?? '—'}</td>
                <td className="px-3 py-2">{s.setupSavedMinutes ?? '—'}</td>
                <td className="px-3 py-2">{pct(s.balanceScore)}</td>
                <td className="px-3 py-2">{pct(s.onTimeRate)}</td>
                <td className="px-3 py-2">{s.createdBy ?? '—'}</td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-500">No hay simulaciones.</td></tr>
            )}
            {loading && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-500">Cargando...</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>{total} simulación(es)</span>
        <div className="flex items-center gap-2">
          <button
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1 border rounded-lg disabled:opacity-50"
          >Anterior</button>
          <span>Página {page} de {totalPages}</span>
          <button
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 border rounded-lg disabled:opacity-50"
          >Siguiente</button>
        </div>
      </div>

      {selectedId != null && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-5">
          {detailLoading && <p className="text-gray-500 text-sm">Cargando detalle...</p>}
          {detailError && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{detailError}</div>}
          {notice && <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-800 text-sm">{notice}</div>}
          {actionError && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{actionError}</div>}

          {detail && (
            <>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  {renaming ? (
                    <div className="flex items-center gap-2">
                      <input
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-72"
                        aria-label="Nuevo nombre"
                      />
                      <button disabled={busy} onClick={handleRename} className="px-3 py-1 bg-purple-600 text-white rounded-lg text-sm disabled:opacity-50">Guardar</button>
                      <button disabled={busy} onClick={() => setRenaming(false)} className="px-3 py-1 bg-gray-100 rounded-lg text-sm disabled:opacity-50">Cancelar</button>
                    </div>
                  ) : (
                    <h2 className="text-lg font-bold text-gray-800">#{detail.id} — {detail.name}</h2>
                  )}
                  <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                    <StatusBadge status={detail.status} />
                    <span>Creada {fmtDate(detail.createdAt)}</span>
                    {detail.parentSimulationId != null && (
                      <button className="underline" onClick={() => selectSimulation(detail.parentSimulationId as number)}>
                        Iteración de #{detail.parentSimulationId}
                      </button>
                    )}
                    {detail.appliedAt && <span>Aplicada {fmtDate(detail.appliedAt)}</span>}
                  </div>
                </div>

                {isSaved && canWrite && !renaming && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {confirming ? (
                      <>
                        <span className="text-sm text-gray-700">
                          {confirming === 'apply'
                            ? `¿Aplicar simulación #${detail.id} «${detail.name}» (${detail.result?.optimized_schedule?.length ?? 0} órdenes)? Cambiará la programación de las órdenes.`
                            : `¿Descartar simulación #${detail.id} «${detail.name}»?`}
                        </span>
                        <button
                          disabled={busy}
                          onClick={confirming === 'apply' ? handleApply : handleDiscard}
                          className={`px-3 py-1 rounded-lg text-sm text-white disabled:opacity-50 ${confirming === 'apply' ? 'bg-purple-600' : 'bg-red-600'}`}
                        >{busy ? 'Procesando...' : 'Confirmar'}</button>
                        <button disabled={busy} onClick={() => setConfirming(null)} className="px-3 py-1 bg-gray-100 rounded-lg text-sm disabled:opacity-50">Cancelar</button>
                      </>
                    ) : (
                      <>
                        <button disabled={busy} onClick={() => { setNameDraft(detail.name); setRenaming(true); setActionError(null); }} className="px-3 py-1 bg-gray-100 rounded-lg text-sm disabled:opacity-50">Renombrar</button>
                        <button disabled={busy || (detail.result?.optimized_schedule?.length ?? 0) === 0} title={(detail.result?.optimized_schedule?.length ?? 0) === 0 ? 'La simulación no tiene órdenes' : undefined} onClick={() => { setActionError(null); setConfirming('apply'); }} className="px-3 py-1 bg-purple-600 text-white rounded-lg text-sm disabled:opacity-50">Aplicar</button>
                        <button disabled={busy} onClick={() => { setActionError(null); setConfirming('discard'); }} className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-sm disabled:opacity-50">Descartar</button>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-1 border-b border-gray-200" role="tablist">
                {([['resumen', 'Resumen'], ['ejecucion', 'Ejecución']] as const).map(([key, label]) => (
                  <button
                    key={key}
                    role="tab"
                    aria-selected={detailTab === key}
                    onClick={() => setDetailTab(key)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                      detailTab === key ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >{label}</button>
                ))}
              </div>

              {detailTab === 'ejecucion' && <SimulationExecutionPanel simulationId={detail.id} />}

              {detailTab === 'resumen' && (
              <div className="space-y-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Metric label="Órdenes" value={String(detail.ordersCount ?? '—')} cls="bg-gray-50 text-gray-700" />
                <Metric label="Setup ahorrado (min)" value={String(detail.setupSavedMinutes ?? '—')} cls="bg-green-50 text-green-600" />
                <Metric label="Balance de carga" value={pct(detail.balanceScore)} cls="bg-blue-50 text-blue-600" />
                <Metric label="Entrega a tiempo" value={pct(detail.onTimeRate)} cls="bg-purple-50 text-purple-600" />
              </div>

              <div className="grid md:grid-cols-3 gap-3 text-sm">
                <TextBlock title="Objetivo" text={detail.objetivo} />
                <TextBlock title="Plan" text={detail.plan} />
                <TextBlock title="Reglas (nivel 3)" text={detail.reglas} />
              </div>

              <div>
                <h3 className="font-semibold text-gray-700 mb-2 text-sm">Reglas nivel 1-2 vigentes en esta corrida</h3>
                {detail.policySnapshot && detail.policySnapshot.length > 0 ? (
                  <ul className="space-y-1 text-sm">
                    {detail.policySnapshot.map((r) => (
                      <li key={r.id} className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                        <span className="text-xs font-semibold text-gray-500 mr-2">N{r.level}</span>
                        <span className="font-medium">{r.title}</span>
                        <p className="text-gray-600">{r.instruction}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">Sin reglas registradas.</p>
                )}
              </div>

              {detail.result?.recommendations && detail.result.recommendations.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <h3 className="font-semibold text-blue-800 mb-1 text-sm">Recomendaciones</h3>
                  <ul className="list-disc list-inside text-sm text-blue-900 space-y-0.5">
                    {detail.result.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}
              {detail.result?.warnings && detail.result.warnings.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <h3 className="font-semibold text-amber-800 mb-1 text-sm">Advertencias</h3>
                  <ul className="list-disc list-inside text-sm text-amber-900 space-y-0.5">
                    {detail.result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              <div>
                <h3 className="font-semibold text-gray-700 mb-2 text-sm">
                  Programación ({detail.result?.optimized_schedule?.length ?? 0} órdenes)
                </h3>
                <div className="border border-gray-200 rounded-xl overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600 text-left sticky top-0">
                      <tr>
                        {['Secuencia', 'Orden', 'Cliente', 'Producto', 'Kilos solicitados', 'Colores', 'Máquina', 'Fecha compromiso entrega', 'Inicio prod.', 'Fin prod.'].map((h) => (
                          <th key={h} className="px-3 py-2 font-semibold whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {detail.result?.optimized_schedule?.map((it, i) => {
                        const d = it.order_details;
                        const customer = d ? [d.customerCode, d.customerName].filter(Boolean).join(' - ') : '';
                        const product = d ? [d.productCode, d.productName].filter(Boolean).join(' - ') : '';
                        return (
                        <tr key={`${it.order_id}-${i}`}>
                          <td className="px-3 py-2">{it.sequence}</td>
                          <td className="px-3 py-2">{it.order_id}</td>
                          <td className="px-3 py-2">
                            {customer || '—'}
                            {d?.branchName && <span className="text-gray-500"> · {d.branchName}</span>}
                          </td>
                          <td className="px-3 py-2">{product || '—'}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            {d?.requestedKilos != null ? `${d.requestedKilos.toLocaleString('es-MX')}${d.unit && d.unit.toLowerCase() !== 'kg' ? ` ${d.unit}` : ''}` : '—'}
                          </td>
                          <td className="px-3 py-2 text-center">{d?.colorsRequired ?? '—'}</td>
                          <td className="px-3 py-2">{it.machine_name || it.assigned_machine}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{fmtDay(d?.dueDate)}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{fmtDate(it.recommended_start)}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{fmtDate(it.recommended_end)}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const Metric: React.FC<{ label: string; value: string; cls: string }> = ({ label, value, cls }) => (
  <div className={`rounded-xl p-4 text-center ${cls}`}>
    <div className="text-2xl font-bold">{value}</div>
    <div className="text-xs mt-1 text-gray-600">{label}</div>
  </div>
);

const TextBlock: React.FC<{ title: string; text: string | null }> = ({ title, text }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
    <h3 className="font-semibold text-gray-700 mb-1">{title}</h3>
    <p className="text-gray-600 whitespace-pre-wrap">{text || '—'}</p>
  </div>
);

export default PlanningSimulations;
